/**
 * IncidentDrawer Component
 * Create/Edit/View Incident drawer
 * BuilderBel Construction Project Management System
 */

import { yupResolver } from '@hookform/resolvers/yup';
import { AlertTriangle, MapPin, Shield, User, X } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Controller, Resolver, useForm } from 'react-hook-form';
import * as Yup from 'yup';
import { useToast } from '../../contexts/ToastContext';
import { useCurrentProjectId } from '../../hooks/useProjectRedux';
import { useCreateIncidentMutation, useUpdateIncidentMutation } from '../../services/incidentApi';
import type {
  Incident,
  IncidentType,
  IncidentSeverity,
  CreateIncidentRequest,
  UpdateIncidentRequest,
} from '../../types/incident';
import { DatePicker, Dropdown, FormSwitch, Input, TextArea, TimePicker } from '../forms';

// ============================================================================
// Props
// ============================================================================

export interface IncidentDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  incident?: Incident | null;
  mode: 'create' | 'edit' | 'view';
  onSuccess?: () => void;
}

// ============================================================================
// Form Data
// ============================================================================

interface IncidentFormData {
  title: string;
  description: string;
  type: IncidentType;
  severity: IncidentSeverity;
  category: string;
  incidentDate: string;
  incidentTime: string;
  location: string;
  specificLocation: string;
  isAnonymous: boolean;
  oshaRecordable: boolean;
  // Injured party fields (shown when type is injury or illness)
  injuredName: string;
  injuredCompany: string;
  injuredTrade: string;
  injuredEmploymentType: string;
  injuryType: string;
  treatmentReceived: string;
  medicalFacility: string;
}

// ============================================================================
// Dropdown Options
// ============================================================================

const TYPE_OPTIONS = [
  { value: 'injury', label: 'Injury' },
  { value: 'illness', label: 'Illness' },
  { value: 'near_miss', label: 'Near Miss' },
  { value: 'property_damage', label: 'Property Damage' },
  { value: 'environmental', label: 'Environmental' },
  { value: 'security', label: 'Security' },
  { value: 'other', label: 'Other' },
];

const SEVERITY_OPTIONS = [
  { value: 'minor', label: 'Minor' },
  { value: 'moderate', label: 'Moderate' },
  { value: 'serious', label: 'Serious' },
  { value: 'severe', label: 'Severe' },
  { value: 'fatal', label: 'Fatal' },
];

const EMPLOYMENT_TYPE_OPTIONS = [
  { value: 'employee', label: 'Employee' },
  { value: 'contractor', label: 'Contractor' },
  { value: 'subcontractor', label: 'Subcontractor' },
  { value: 'visitor', label: 'Visitor' },
  { value: 'other', label: 'Other' },
];

const TREATMENT_OPTIONS = [
  { value: 'first_aid_only', label: 'First Aid Only' },
  { value: 'medical_treatment', label: 'Medical Treatment' },
  { value: 'hospitalization', label: 'Hospitalization' },
  { value: 'no_treatment', label: 'No Treatment Required' },
];

const COMMON_CATEGORIES = [
  { value: 'Slip/Trip/Fall', label: 'Slip/Trip/Fall' },
  { value: 'Struck By', label: 'Struck By' },
  { value: 'Caught In/Between', label: 'Caught In/Between' },
  { value: 'Fall From Elevation', label: 'Fall From Elevation' },
  { value: 'Electrical', label: 'Electrical' },
  { value: 'Chemical Exposure', label: 'Chemical Exposure' },
  { value: 'Overexertion', label: 'Overexertion' },
  { value: 'Equipment/Machinery', label: 'Equipment/Machinery' },
  { value: 'Fire/Explosion', label: 'Fire/Explosion' },
  { value: 'Vehicle Accident', label: 'Vehicle Accident' },
  { value: 'Environmental Hazard', label: 'Environmental Hazard' },
  { value: 'Other', label: 'Other' },
];

// ============================================================================
// Validation Schema
// ============================================================================

const validationSchema = Yup.object({
  title: Yup.string().required('Title is required').max(500, 'Title cannot exceed 500 characters'),
  description: Yup.string()
    .required('Description is required')
    .max(5000, 'Description cannot exceed 5000 characters'),
  type: Yup.string().required('Incident type is required'),
  severity: Yup.string().required('Severity is required'),
  incidentDate: Yup.string().required('Incident date is required'),
  location: Yup.string()
    .required('Location is required')
    .max(500, 'Location cannot exceed 500 characters'),
  category: Yup.string().optional(),
  incidentTime: Yup.string().optional(),
  specificLocation: Yup.string().optional(),
  isAnonymous: Yup.boolean(),
  oshaRecordable: Yup.boolean(),
  injuredName: Yup.string().optional(),
  injuredCompany: Yup.string().optional(),
  injuredTrade: Yup.string().optional(),
  injuredEmploymentType: Yup.string().optional(),
  injuryType: Yup.string().optional(),
  treatmentReceived: Yup.string().optional(),
  medicalFacility: Yup.string().optional(),
});

// ============================================================================
// Severity Color Mapping
// ============================================================================

const SEVERITY_COLORS: Record<IncidentSeverity, string> = {
  minor: 'bg-green-100 text-green-700',
  moderate: 'bg-yellow-100 text-yellow-700',
  serious: 'bg-orange-100 text-orange-700',
  severe: 'bg-red-100 text-red-700',
  fatal: 'bg-gray-900 text-white',
};

// ============================================================================
// Section Header Component
// ============================================================================

const SectionHeader: React.FC<{
  icon: React.ReactNode;
  title: string;
}> = ({ icon, title }) => (
  <div className="flex items-center gap-2 mb-4">
    <div className="flex-shrink-0 text-brand-600">{icon}</div>
    <h3 className="text-sm font-semibold text-gray-800 uppercase tracking-wide">{title}</h3>
    <div className="flex-1 border-t border-gray-200" />
  </div>
);

// ============================================================================
// Main Component
// ============================================================================

const IncidentDrawer: React.FC<IncidentDrawerProps> = ({
  isOpen,
  onClose,
  incident,
  mode,
  onSuccess,
}) => {
  const projectId = useCurrentProjectId();
  const toast = useToast();
  const [activeSection, setActiveSection] = useState<string>('basic');

  const isReadOnly = mode === 'view';

  const [createIncident, { isLoading: isCreating }] = useCreateIncidentMutation();
  const [updateIncident, { isLoading: isUpdating }] = useUpdateIncidentMutation();

  const isSaving = isCreating || isUpdating;

  const {
    register,
    handleSubmit,
    control,
    watch,
    reset,
    formState: { errors },
  } = useForm<IncidentFormData>({
    resolver: yupResolver(validationSchema) as unknown as Resolver<IncidentFormData>,
    defaultValues: {
      title: '',
      description: '',
      type: 'near_miss',
      severity: 'minor',
      category: '',
      incidentDate: new Date().toISOString().split('T')[0],
      incidentTime: '',
      location: '',
      specificLocation: '',
      isAnonymous: false,
      oshaRecordable: false,
      injuredName: '',
      injuredCompany: '',
      injuredTrade: '',
      injuredEmploymentType: '',
      injuryType: '',
      treatmentReceived: '',
      medicalFacility: '',
    },
  });

  const watchType = watch('type');
  const watchSeverity = watch('severity');
  const showInjuredParty = watchType === 'injury' || watchType === 'illness';

  // Populate form when editing
  useEffect(() => {
    if (incident && (mode === 'edit' || mode === 'view')) {
      reset({
        title: incident.title,
        description: incident.description,
        type: incident.type,
        severity: incident.severity,
        category: incident.category || '',
        incidentDate: incident.incidentDate
          ? new Date(incident.incidentDate).toISOString().split('T')[0]
          : '',
        incidentTime: incident.incidentTime || '',
        location: incident.location,
        specificLocation: incident.specificLocation || '',
        isAnonymous: incident.isAnonymous,
        oshaRecordable: incident.oshaRecordable,
        injuredName: incident.injuredParty?.name || '',
        injuredCompany: incident.injuredParty?.company || '',
        injuredTrade: incident.injuredParty?.trade || '',
        injuredEmploymentType: incident.injuredParty?.employmentType || '',
        injuryType: incident.injuredParty?.injuryType || '',
        treatmentReceived: incident.injuredParty?.treatmentReceived || '',
        medicalFacility: incident.injuredParty?.medicalFacility || '',
      });
    } else if (mode === 'create') {
      reset({
        title: '',
        description: '',
        type: 'near_miss',
        severity: 'minor',
        category: '',
        incidentDate: new Date().toISOString().split('T')[0],
        incidentTime: '',
        location: '',
        specificLocation: '',
        isAnonymous: false,
        oshaRecordable: false,
        injuredName: '',
        injuredCompany: '',
        injuredTrade: '',
        injuredEmploymentType: '',
        injuryType: '',
        treatmentReceived: '',
        medicalFacility: '',
      });
    }
  }, [incident, mode, reset]);

  const onSubmit = async (formData: IncidentFormData): Promise<void> => {
    if (!projectId) {
      toast.error('No project selected');
      return;
    }

    const injuredParty = showInjuredParty
      ? {
          name: formData.injuredName || undefined,
          company: formData.injuredCompany || undefined,
          trade: formData.injuredTrade || undefined,
          employmentType: formData.injuredEmploymentType || undefined,
          injuryType: formData.injuryType || undefined,
          treatmentReceived: formData.treatmentReceived || undefined,
          medicalFacility: formData.medicalFacility || undefined,
        }
      : undefined;

    try {
      if (mode === 'create') {
        const payload: CreateIncidentRequest = {
          title: formData.title,
          description: formData.description,
          type: formData.type,
          severity: formData.severity,
          category: formData.category || undefined,
          incidentDate: formData.incidentDate,
          incidentTime: formData.incidentTime || undefined,
          location: formData.location,
          specificLocation: formData.specificLocation || undefined,
          isAnonymous: formData.isAnonymous,
          oshaRecordable: formData.oshaRecordable,
          injuredParty,
        };

        await createIncident({ projectId, data: payload }).unwrap();
        toast.success('Incident reported successfully');
      } else if (mode === 'edit' && incident) {
        const payload: UpdateIncidentRequest = {
          title: formData.title,
          description: formData.description,
          type: formData.type,
          severity: formData.severity,
          category: formData.category || undefined,
          incidentDate: formData.incidentDate,
          incidentTime: formData.incidentTime || undefined,
          location: formData.location,
          specificLocation: formData.specificLocation || undefined,
          isAnonymous: formData.isAnonymous,
          oshaRecordable: formData.oshaRecordable,
          injuredParty,
        };

        await updateIncident({ projectId, incidentId: incident._id, data: payload }).unwrap();
        toast.success('Incident updated successfully');
      }

      onSuccess?.();
      onClose();
    } catch {
      toast.error(mode === 'create' ? 'Failed to report incident' : 'Failed to update incident');
    }
  };

  if (!isOpen) return null;

  const drawerTitle =
    mode === 'create'
      ? 'Report Incident'
      : mode === 'edit'
        ? `Edit ${incident?.incidentNumber || 'Incident'}`
        : incident?.incidentNumber || 'Incident Details';

  return createPortal(
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/30 backdrop-blur-sm z-[90]"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer */}
      <div className="fixed top-6 right-6 bottom-6 z-[100] inset-6 animate-in slide-in-from-right">
        <div className="bg-white rounded-2xl shadow-2xl h-full flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-8 py-5 border-b border-gray-200 flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-brand-50 rounded-lg">
                <AlertTriangle className="h-5 w-5 text-brand-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">{drawerTitle}</h2>
                {incident && mode !== 'create' && (
                  <span
                    className={`text-xs font-medium px-2 py-0.5 rounded-full ${SEVERITY_COLORS[incident.severity]}`}
                  >
                    {incident.severity.charAt(0).toUpperCase() + incident.severity.slice(1)}
                  </span>
                )}
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label="Close drawer"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Section Tabs (view mode) */}
          {mode === 'view' && (
            <div className="flex border-b border-gray-200 px-8 flex-shrink-0">
              {[
                { key: 'basic', label: 'Details' },
                { key: 'injured', label: 'Injured Party' },
                { key: 'witnesses', label: `Witnesses (${incident?.witnesses?.length ?? 0})` },
                { key: 'actions', label: `Actions (${incident?.correctiveActions?.length ?? 0})` },
              ].map(tab => (
                <button
                  key={tab.key}
                  onClick={() => setActiveSection(tab.key)}
                  className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                    activeSection === tab.key
                      ? 'border-brand-600 text-brand-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          )}

          {/* Content */}
          <div className="flex-1 overflow-y-auto px-8 py-6">
            {mode === 'view' && incident ? (
              /* View Mode */
              <div className="space-y-6">
                {activeSection === 'basic' && (
                  <>
                    <SectionHeader
                      icon={<AlertTriangle className="h-4 w-4" />}
                      title="Incident Information"
                    />
                    <dl className="grid grid-cols-2 gap-4">
                      <div className="col-span-2">
                        <dt className="text-xs text-gray-500 mb-1">Title</dt>
                        <dd className="text-sm font-medium text-gray-900">{incident.title}</dd>
                      </div>
                      <div>
                        <dt className="text-xs text-gray-500 mb-1">Type</dt>
                        <dd className="text-sm text-gray-900 capitalize">
                          {incident.type.replace(/_/g, ' ')}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-xs text-gray-500 mb-1">Severity</dt>
                        <dd>
                          <span
                            className={`text-xs font-medium px-2 py-1 rounded-full ${SEVERITY_COLORS[incident.severity]}`}
                          >
                            {incident.severity}
                          </span>
                        </dd>
                      </div>
                      <div>
                        <dt className="text-xs text-gray-500 mb-1">Date</dt>
                        <dd className="text-sm text-gray-900">
                          {new Date(incident.incidentDate).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                          })}
                          {incident.incidentTime && ` at ${incident.incidentTime}`}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-xs text-gray-500 mb-1">Status</dt>
                        <dd className="text-sm text-gray-900 capitalize">
                          {incident.status.replace(/_/g, ' ')}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-xs text-gray-500 mb-1">Location</dt>
                        <dd className="text-sm text-gray-900">{incident.location}</dd>
                      </div>
                      {incident.category && (
                        <div>
                          <dt className="text-xs text-gray-500 mb-1">Category</dt>
                          <dd className="text-sm text-gray-900">{incident.category}</dd>
                        </div>
                      )}
                      <div className="col-span-2">
                        <dt className="text-xs text-gray-500 mb-1">Description</dt>
                        <dd className="text-sm text-gray-700 whitespace-pre-wrap">
                          {incident.description}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-xs text-gray-500 mb-1">OSHA Recordable</dt>
                        <dd className="text-sm text-gray-900">
                          {incident.oshaRecordable ? 'Yes' : 'No'}
                          {incident.oshaClassification &&
                            ` — ${incident.oshaClassification.replace(/_/g, ' ')}`}
                        </dd>
                      </div>
                    </dl>
                  </>
                )}

                {activeSection === 'injured' && (
                  <>
                    <SectionHeader icon={<User className="h-4 w-4" />} title="Injured Party" />
                    {incident.injuredParty?.name ? (
                      <dl className="grid grid-cols-2 gap-4">
                        <div>
                          <dt className="text-xs text-gray-500 mb-1">Name</dt>
                          <dd className="text-sm text-gray-900">{incident.injuredParty.name}</dd>
                        </div>
                        <div>
                          <dt className="text-xs text-gray-500 mb-1">Company</dt>
                          <dd className="text-sm text-gray-900">
                            {incident.injuredParty.company || '-'}
                          </dd>
                        </div>
                        <div>
                          <dt className="text-xs text-gray-500 mb-1">Trade</dt>
                          <dd className="text-sm text-gray-900">
                            {incident.injuredParty.trade || '-'}
                          </dd>
                        </div>
                        <div>
                          <dt className="text-xs text-gray-500 mb-1">Employment Type</dt>
                          <dd className="text-sm text-gray-900 capitalize">
                            {incident.injuredParty.employmentType || '-'}
                          </dd>
                        </div>
                        <div>
                          <dt className="text-xs text-gray-500 mb-1">Injury Type</dt>
                          <dd className="text-sm text-gray-900">
                            {incident.injuredParty.injuryType || '-'}
                          </dd>
                        </div>
                        <div>
                          <dt className="text-xs text-gray-500 mb-1">Treatment Received</dt>
                          <dd className="text-sm text-gray-900">
                            {incident.injuredParty.treatmentReceived || '-'}
                          </dd>
                        </div>
                        {incident.injuredParty.bodyPart &&
                          incident.injuredParty.bodyPart.length > 0 && (
                            <div className="col-span-2">
                              <dt className="text-xs text-gray-500 mb-1">Body Parts Affected</dt>
                              <dd className="text-sm text-gray-900">
                                {incident.injuredParty.bodyPart.join(', ')}
                              </dd>
                            </div>
                          )}
                      </dl>
                    ) : (
                      <p className="text-sm text-gray-500">
                        No injured party information recorded.
                      </p>
                    )}
                  </>
                )}

                {activeSection === 'witnesses' && (
                  <>
                    <SectionHeader icon={<User className="h-4 w-4" />} title="Witnesses" />
                    {incident.witnesses && incident.witnesses.length > 0 ? (
                      <div className="space-y-3">
                        {incident.witnesses.map((witness, idx) => (
                          <div key={witness._id || idx} className="p-4 bg-gray-50 rounded-lg">
                            <p className="text-sm font-medium text-gray-900">{witness.name}</p>
                            {witness.company && (
                              <p className="text-xs text-gray-500 mt-0.5">{witness.company}</p>
                            )}
                            {witness.statement && (
                              <p className="text-sm text-gray-700 mt-2 italic">
                                "{witness.statement}"
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500">No witnesses recorded.</p>
                    )}
                  </>
                )}

                {activeSection === 'actions' && (
                  <>
                    <SectionHeader
                      icon={<Shield className="h-4 w-4" />}
                      title="Corrective Actions"
                    />
                    {incident.correctiveActions && incident.correctiveActions.length > 0 ? (
                      <div className="space-y-3">
                        {incident.correctiveActions.map((action, idx) => (
                          <div key={action._id || idx} className="p-4 bg-gray-50 rounded-lg">
                            <div className="flex items-start justify-between gap-2">
                              <p className="text-sm text-gray-900">{action.description}</p>
                              <span
                                className={`text-xs px-2 py-0.5 rounded-full flex-shrink-0 ${
                                  action.status === 'verified'
                                    ? 'bg-green-100 text-green-700'
                                    : action.status === 'completed'
                                      ? 'bg-blue-100 text-blue-700'
                                      : action.status === 'in_progress'
                                        ? 'bg-yellow-100 text-yellow-700'
                                        : 'bg-gray-100 text-gray-700'
                                }`}
                              >
                                {action.status.replace(/_/g, ' ')}
                              </span>
                            </div>
                            <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                              <span className="capitalize">{action.type.replace(/_/g, ' ')}</span>
                              <span className="capitalize">{action.priority} priority</span>
                              {action.assignedToName && <span>{action.assignedToName}</span>}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500">No corrective actions recorded.</p>
                    )}
                  </>
                )}
              </div>
            ) : (
              /* Create/Edit Form */
              <form
                id="incident-form"
                onSubmit={handleSubmit(onSubmit as Parameters<typeof handleSubmit>[0])}
                className="space-y-8"
              >
                {/* Basic Info Section */}
                <div>
                  <SectionHeader
                    icon={<AlertTriangle className="h-4 w-4" />}
                    title="Basic Information"
                  />
                  <div className="space-y-4">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">
                        Title <span className="text-red-500">*</span>
                      </label>
                      <Input
                        {...register('title')}
                        placeholder="Brief incident title"
                        disabled={isReadOnly}
                        className={errors.title ? 'border-red-500' : ''}
                      />
                      {errors.title && (
                        <p className="mt-1 text-xs text-red-500">{errors.title.message}</p>
                      )}
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">
                          Type <span className="text-red-500">*</span>
                        </label>
                        <Controller
                          name="type"
                          control={control}
                          render={({ field }) => (
                            <Dropdown
                              value={field.value}
                              onChange={val => field.onChange(val)}
                              options={TYPE_OPTIONS}
                              placeholder="Select type"
                              disabled={isReadOnly}
                            />
                          )}
                        />
                        {errors.type && (
                          <p className="mt-1 text-xs text-red-500">{errors.type.message}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-xs text-gray-500 mb-1">
                          Severity <span className="text-red-500">*</span>
                        </label>
                        <Controller
                          name="severity"
                          control={control}
                          render={({ field }) => (
                            <Dropdown
                              value={field.value}
                              onChange={val => field.onChange(val)}
                              options={SEVERITY_OPTIONS}
                              placeholder="Select severity"
                              disabled={isReadOnly}
                            />
                          )}
                        />
                        {errors.severity && (
                          <p className="mt-1 text-xs text-red-500">{errors.severity.message}</p>
                        )}
                      </div>
                    </div>

                    {watchSeverity && (
                      <div
                        className={`text-xs px-3 py-2 rounded-lg font-medium ${SEVERITY_COLORS[watchSeverity as IncidentSeverity] || 'bg-gray-100 text-gray-700'}`}
                      >
                        Severity: {watchSeverity.charAt(0).toUpperCase() + watchSeverity.slice(1)}
                        {watchSeverity === 'fatal' && ' — OSHA reportable (death)'}
                        {watchSeverity === 'severe' && ' — OSHA recordable (days away from work)'}
                        {watchSeverity === 'serious' && ' — OSHA recordable (restricted duty)'}
                      </div>
                    )}

                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Category</label>
                      <Controller
                        name="category"
                        control={control}
                        render={({ field }) => (
                          <Dropdown
                            value={field.value}
                            onChange={val => field.onChange(val)}
                            options={COMMON_CATEGORIES}
                            placeholder="Select category (optional)"
                            disabled={isReadOnly}
                          />
                        )}
                      />
                    </div>

                    <div>
                      <label className="block text-xs text-gray-500 mb-1">
                        Description <span className="text-red-500">*</span>
                      </label>
                      <Controller
                        name="description"
                        control={control}
                        render={({ field }) => (
                          <TextArea
                            value={field.value}
                            onChange={val => field.onChange(val)}
                            placeholder="Detailed description of what happened"
                            rows={4}
                            disabled={isReadOnly}
                            className={errors.description ? 'border-red-500' : ''}
                          />
                        )}
                      />
                      {errors.description && (
                        <p className="mt-1 text-xs text-red-500">{errors.description.message}</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Date & Location Section */}
                <div>
                  <SectionHeader icon={<MapPin className="h-4 w-4" />} title="Date & Location" />
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">
                          Incident Date <span className="text-red-500">*</span>
                        </label>
                        <Controller
                          name="incidentDate"
                          control={control}
                          render={({ field }) => (
                            <DatePicker
                              value={field.value}
                              onChange={val => field.onChange(val)}
                              disabled={isReadOnly}
                            />
                          )}
                        />
                        {errors.incidentDate && (
                          <p className="mt-1 text-xs text-red-500">{errors.incidentDate.message}</p>
                        )}
                      </div>

                      <div>
                        <Controller
                          name="incidentTime"
                          control={control}
                          render={({ field }) => (
                            <TimePicker
                              label="Time (optional)"
                              value={field.value}
                              onChange={val => field.onChange(val)}
                              disabled={isReadOnly}
                              placeholder="Select time"
                            />
                          )}
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs text-gray-500 mb-1">
                        Location <span className="text-red-500">*</span>
                      </label>
                      <Input
                        {...register('location')}
                        placeholder="General location (e.g., Building A, Floor 3)"
                        disabled={isReadOnly}
                        className={errors.location ? 'border-red-500' : ''}
                      />
                      {errors.location && (
                        <p className="mt-1 text-xs text-red-500">{errors.location.message}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-xs text-gray-500 mb-1">
                        Specific Location (optional)
                      </label>
                      <Input
                        {...register('specificLocation')}
                        placeholder="More specific location details"
                        disabled={isReadOnly}
                      />
                    </div>
                  </div>
                </div>

                {/* Injured Party Section (conditional) */}
                {showInjuredParty && (
                  <div>
                    <SectionHeader icon={<User className="h-4 w-4" />} title="Injured Party" />
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Name</label>
                          <Input
                            {...register('injuredName')}
                            placeholder="Injured party name"
                            disabled={isReadOnly}
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Company</label>
                          <Input
                            {...register('injuredCompany')}
                            placeholder="Employer/company"
                            disabled={isReadOnly}
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Trade</label>
                          <Input
                            {...register('injuredTrade')}
                            placeholder="Trade or occupation"
                            disabled={isReadOnly}
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">
                            Employment Type
                          </label>
                          <Controller
                            name="injuredEmploymentType"
                            control={control}
                            render={({ field }) => (
                              <Dropdown
                                value={field.value}
                                onChange={val => field.onChange(val)}
                                options={EMPLOYMENT_TYPE_OPTIONS}
                                placeholder="Select type"
                                disabled={isReadOnly}
                              />
                            )}
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Injury Type</label>
                          <Input
                            {...register('injuryType')}
                            placeholder="e.g., Laceration, Fracture"
                            disabled={isReadOnly}
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">
                            Treatment Received
                          </label>
                          <Controller
                            name="treatmentReceived"
                            control={control}
                            render={({ field }) => (
                              <Dropdown
                                value={field.value}
                                onChange={val => field.onChange(val)}
                                options={TREATMENT_OPTIONS}
                                placeholder="Select treatment"
                                disabled={isReadOnly}
                              />
                            )}
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-xs text-gray-500 mb-1">
                          Medical Facility (if applicable)
                        </label>
                        <Input
                          {...register('medicalFacility')}
                          placeholder="Hospital or clinic name"
                          disabled={isReadOnly}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* OSHA / Reporting Section */}
                <div>
                  <SectionHeader icon={<Shield className="h-4 w-4" />} title="Reporting Options" />
                  <div className="space-y-3">
                    <Controller
                      name="oshaRecordable"
                      control={control}
                      render={({ field }) => (
                        <FormSwitch
                          label="OSHA Recordable"
                          description="Mark if this incident must be recorded on the OSHA 300 Log"
                          checked={!!field.value}
                          onChange={field.onChange}
                          disabled={isReadOnly}
                          size="sm"
                        />
                      )}
                    />

                    <Controller
                      name="isAnonymous"
                      control={control}
                      render={({ field }) => (
                        <FormSwitch
                          label="Anonymous Report"
                          description="Hide your identity from this incident report"
                          checked={!!field.value}
                          onChange={field.onChange}
                          disabled={isReadOnly}
                          size="sm"
                        />
                      )}
                    />
                  </div>
                </div>
              </form>
            )}
          </div>

          {/* Footer */}
          {mode !== 'view' && (
            <div className="border-t border-gray-200 px-8 py-5 bg-gray-50 flex items-center justify-end space-x-3 flex-shrink-0 rounded-b-2xl">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                form="incident-form"
                disabled={isSaving}
                className="px-4 py-2 text-sm font-medium text-white bg-brand-600 rounded-lg hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isSaving ? 'Saving...' : mode === 'create' ? 'Report Incident' : 'Save Changes'}
              </button>
            </div>
          )}
        </div>
      </div>
    </>,
    document.body
  );
};

export default IncidentDrawer;
